import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { API } from '@/App';
import { RefreshCw, AlertCircle, Users, Eye, Loader2, Bug } from 'lucide-react';

function DebugPage() {
  const [errors, setErrors] = useState([]);
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('errors');

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setIsLoading(true);
    await Promise.all([fetchErrors(), fetchStats()]);
    setIsLoading(false);
  };

  const fetchErrors = async () => {
    try {
      const res = await fetch(`${API}/debug/errors?limit=200`);
      if (res.ok) setErrors(await res.json());
    } catch {}
  };

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API}/debug/stats`);
      if (res.ok) setStats(await res.json());
    } catch {}
  };

  const formatDate = (ts) => {
    if (!ts) return '-';
    const d = new Date(ts);
    return d.toLocaleString('en-ZA', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="text-muted-foreground">Loading debug data...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 md:p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-2">
            <Bug className="h-6 w-6 text-red-400" /> Debug Console
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Records kept for 2 days. Auto-cleanup on load.</p>
        </div>
        <Button onClick={fetchAll} variant="outline" className="rounded-full" data-testid="refresh-debug-btn">
          <RefreshCw className="h-4 w-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card className="p-4 bg-red-500/10 border-red-500/30">
          <p className="text-xs text-muted-foreground">Total Errors</p>
          <p className="text-2xl font-bold text-red-400" data-testid="total-errors">{stats?.total_errors || 0}</p>
        </Card>
        <Card className="p-4 bg-blue-500/10 border-blue-500/30">
          <p className="text-xs text-muted-foreground">Total Logins</p>
          <p className="text-2xl font-bold text-blue-400" data-testid="total-logins">{stats?.total_logins || 0}</p>
        </Card>
        <Card className="p-4 bg-green-500/10 border-green-500/30">
          <p className="text-xs text-muted-foreground">Unique Users</p>
          <p className="text-2xl font-bold text-green-400" data-testid="unique-users">{stats?.unique_users_logged_in || 0}</p>
        </Card>
        <Card className="p-4 bg-purple-500/10 border-purple-500/30">
          <p className="text-xs text-muted-foreground">Pages Tracked</p>
          <p className="text-2xl font-bold text-purple-400" data-testid="pages-tracked">{stats?.top_pages?.length || 0}</p>
        </Card>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-4">
        <Button
          onClick={() => setActiveTab('errors')}
          className={`rounded-lg text-sm ${activeTab === 'errors' ? 'bg-red-600 text-white' : 'bg-zinc-800 hover:bg-zinc-700'}`}
          data-testid="tab-errors"
        >
          <AlertCircle className="h-4 w-4 mr-1" /> Errors ({errors.length})
        </Button>
        <Button
          onClick={() => setActiveTab('logins')}
          className={`rounded-lg text-sm ${activeTab === 'logins' ? 'bg-blue-600 text-white' : 'bg-zinc-800 hover:bg-zinc-700'}`}
          data-testid="tab-logins"
        >
          <Users className="h-4 w-4 mr-1" /> Logins ({stats?.total_logins || 0})
        </Button>
        <Button
          onClick={() => setActiveTab('pages')}
          className={`rounded-lg text-sm ${activeTab === 'pages' ? 'bg-green-600 text-white' : 'bg-zinc-800 hover:bg-zinc-700'}`}
          data-testid="tab-pages"
        >
          <Eye className="h-4 w-4 mr-1" /> Top Pages
        </Button>
      </div>

      {/* Error Logs Table */}
      {activeTab === 'errors' && (
        <Card className="overflow-hidden rounded-xl" data-testid="errors-table">
          <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-red-900/50 sticky top-0 z-10">
                <tr>
                  <th className="px-3 py-3 text-left text-white font-medium whitespace-nowrap">Date & Time</th>
                  <th className="px-3 py-3 text-left text-white font-medium">User</th>
                  <th className="px-3 py-3 text-left text-white font-medium">URL</th>
                  <th className="px-3 py-3 text-left text-white font-medium">Data Entered</th>
                  <th className="px-3 py-3 text-left text-white font-medium">Error</th>
                </tr>
              </thead>
              <tbody>
                {errors.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                      No errors recorded in the last 2 days
                    </td>
                  </tr>
                ) : (
                  errors.map((err, idx) => (
                    <tr key={idx} className={`border-b border-white/5 ${idx % 2 === 0 ? 'bg-zinc-900' : 'bg-zinc-900/50'} hover:bg-zinc-800`}>
                      <td className="px-3 py-2 text-xs text-gray-300 whitespace-nowrap">{formatDate(err.timestamp)}</td>
                      <td className="px-3 py-2 text-xs text-gray-300 max-w-[120px] truncate">{err.user || '-'}</td>
                      <td className="px-3 py-2 text-xs font-mono text-yellow-300 max-w-[200px] truncate">{err.url || '-'}</td>
                      <td className="px-3 py-2 text-xs text-gray-400 max-w-[200px] truncate">{err.data_entered || '-'}</td>
                      <td className="px-3 py-2 text-xs text-red-400 max-w-[300px]">{err.error || '-'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Logins Table */}
      {activeTab === 'logins' && (
        <Card className="overflow-hidden rounded-xl" data-testid="logins-table">
          <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-blue-900/50 sticky top-0 z-10">
                <tr>
                  <th className="px-4 py-3 text-left text-white font-medium">Date & Time</th>
                  <th className="px-4 py-3 text-left text-white font-medium">User</th>
                  <th className="px-4 py-3 text-left text-white font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {(!stats?.recent_logins || stats.recent_logins.length === 0) ? (
                  <tr>
                    <td colSpan={3} className="px-4 py-8 text-center text-muted-foreground">
                      No logins recorded in the last 2 days
                    </td>
                  </tr>
                ) : (
                  stats.recent_logins.map((login, idx) => (
                    <tr key={idx} className={`border-b border-white/5 ${idx % 2 === 0 ? 'bg-zinc-900' : 'bg-zinc-900/50'}`}>
                      <td className="px-4 py-2 text-xs text-gray-300 whitespace-nowrap">{formatDate(login.timestamp)}</td>
                      <td className="px-4 py-2 text-sm text-white">{login.user || '-'}</td>
                      <td className="px-4 py-2">
                        <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full">Success</span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Top Pages Table */}
      {activeTab === 'pages' && (
        <Card className="overflow-hidden rounded-xl" data-testid="pages-table">
          <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-green-900/50 sticky top-0 z-10">
                <tr>
                  <th className="px-4 py-3 text-left text-white font-medium w-12">#</th>
                  <th className="px-4 py-3 text-left text-white font-medium">Page URL</th>
                  <th className="px-4 py-3 text-right text-white font-medium">Visits</th>
                  <th className="px-4 py-3 text-left text-white font-medium">Bar</th>
                </tr>
              </thead>
              <tbody>
                {(!stats?.top_pages || stats.top_pages.length === 0) ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-8 text-center text-muted-foreground">
                      No page visits recorded in the last 2 days
                    </td>
                  </tr>
                ) : (
                  stats.top_pages.map((page, idx) => {
                    const maxVisits = stats.top_pages[0]?.visits || 1;
                    const pct = Math.round((page.visits / maxVisits) * 100);
                    return (
                      <tr key={idx} className={`border-b border-white/5 ${idx % 2 === 0 ? 'bg-zinc-900' : 'bg-zinc-900/50'}`}>
                        <td className="px-4 py-2 text-xs text-gray-500">{idx + 1}</td>
                        <td className="px-4 py-2 text-sm font-mono text-green-300">{page.url}</td>
                        <td className="px-4 py-2 text-sm text-white text-right font-bold">{page.visits}</td>
                        <td className="px-4 py-2 w-40">
                          <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
                            <div className="h-full bg-green-500 rounded-full" style={{ width: `${pct}%` }} />
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

export default DebugPage;
